import React, { useCallback, useRef } from "react";
import { List } from "../../Components/common/List/List";
import { ListProvider } from "../../Components/common/List/Providers/List";
import { useListProps } from "./utilities/list";
import { useIndexContractsContext } from "./providers/Index";
import { Contract } from "../../Models/Contract";
import { useAppContext } from "../../AppProvider";
import { useTypes } from "../../utilities/types";
import { createModalConfig, ModalConfig } from "../../Models/ModalConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import {
  faPenToSquare,
  faTrash,
  faClone,
} from "../../utilities/fontawesomeIcons";
import { Modal, ModalBody, UncontrolledTooltip } from "reactstrap";
import moment from "moment";
import Delete from "./Delete";
import { DeleteContractProvider } from "./providers/Delete";
import { HandlerContractProvider } from "./providers/Handler";
import Handler from "./Handler";
import { getBadge } from "../../Components/Badges/utilities";

const AnyListProvider = ListProvider as any;
const AnyList = List as any;

export const Contracts: React.FC = () => {
  const listRef = useRef<{ refresh: () => void } | null>(null);
  const listProps = useListProps();
  const indexContractsContext = useIndexContractsContext();
  const modalConfig = indexContractsContext?.modalConfig;
  const setModalConfig = indexContractsContext?.setModalConfig;

  const renderItem = useCallback((item: Contract, index: number) => {
    return <ListItem listRef={listRef} item={item} index={index} />;
  }, []);

  const closeModal = () => {
    if (setModalConfig) {
      setModalConfig(createModalConfig());
    }
  };

  return (
    <>
      <AnyListProvider
        path={listProps.path}
        sorting={listProps.sorting}
        filters={listProps.filters}
      >
        <AnyList
          ref={listRef}
          title={listProps.title}
          create={listProps.create}
          banner={listProps.banner}
          omnisearch={listProps.omnisearch}
          massiveActions={listProps.massiveActions}
          renderItem={renderItem}
          pagination={listProps.pagination}
          selectRowsEnabled={listProps.selectRowsEnabled}
        />
      </AnyListProvider>

      <Modal isOpen={modalConfig?.isOpen} size={modalConfig?.size}>
        <ModalBody className={modalConfig?.className || ""}>
          {modalConfig?.action === "create" && (
            <HandlerContractProvider
              autosave={true}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
              action={"create"}
            >
              <Handler />
            </HandlerContractProvider>
          )}
          {modalConfig?.action == "edit" && (
            <HandlerContractProvider
              autosave={true}
              id={modalConfig?.data?.[0]}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
              action={"edit"}
            >
              <Handler />
            </HandlerContractProvider>
          )}

          {modalConfig.action === "delete" && (
            <DeleteContractProvider
              data={modalConfig?.data}
              callback={() => {
                listRef.current?.refresh();
                closeModal();
              }}
            >
              <Delete />
            </DeleteContractProvider>
          )}

          {modalConfig?.action === "clone" && (
            <HandlerContractProvider
              autosave={true}
              id={modalConfig?.data?.[0]}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
              action={"clone"}
            >
              <Handler />
            </HandlerContractProvider>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default Contracts;

const ListItem: React.FC<{
  listRef: React.RefObject<{
    refresh: () => void;
  } | null>;
  item: Contract;
  index: number;
}> = React.memo(({ item, index }) => {
  const { dictionary, breakpoint } = useAppContext();
  const indexContractsContext = useIndexContractsContext();
  const { booleanOptions, contractTypes, shipments } = useTypes();

  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data?: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = ""
    ) => {
      indexContractsContext?.setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [indexContractsContext]
  );

  const icons = {
    enabled: item.active
      ? booleanOptions.find((o) => o.value === "TRUE")
      : booleanOptions.find((o) => o.value === "FALSE"),
    contractType: contractTypes.find((o) => o.value === item.type),
    shipment: shipments.find((o) => o.value === item.shipmentType),
  };

  const isSmall = ["xs", "sm"].includes(breakpoint);

  const gridTemplateColumns = isSmall
    ? "0.25fr 1fr 1.25fr"
    : "0.5fr 0.6fr 0.5fr 1.25fr 1.75fr 0.9fr 0.4fr auto";

  const gridTemplateAreas = isSmall
    ? `
      " icon owner carrier "
      " icon name description  "
      " icon validity service "
      " row row row "
      " . action action"
    `
    : `
      "icon owner carrier name description validity service action"
    `;

  return (
    <div className="list-item px-2">
      <div
        className="d-grid grid-column align-items-center"
        style={{
          gridTemplateColumns: gridTemplateColumns,
          gridTemplateAreas: gridTemplateAreas,
          gap: "0.5rem 1rem",
        }}
      >
        <div style={{ gridArea: "icon" }}>
          <div className="d-flex">
            <div className="me-2">
              <FontAwesomeIcon
                icon={icons?.enabled?.icon}
                title={`${dictionary.words.active}: ${icons.enabled?.label}`}
                color={icons.enabled?.color}
              />
            </div>

            <div>
              {item.shipmentType === "EXPRESS_RETURN" ? (
                <FontAwesomeIcon
                  icon={icons?.shipment?.icon}
                  title={`${dictionary.shipments.shipment_typology}: ${icons.shipment?.label}`}
                  color={icons.shipment?.color}
                  flip="horizontal"
                />
              ) : (
                <FontAwesomeIcon
                  icon={icons?.shipment?.icon}
                  title={`${dictionary.shipments.shipment_typology}: ${icons.shipment?.label}`}
                  color={icons.shipment?.color}
                />
              )}
            </div>
          </div>
          <div>
            {getBadge({
              text: icons.contractType?.label,
              bgColor: icons.contractType?.color,
            })}
          </div>
        </div>

        <div style={{ gridArea: "owner" }}>
          <label className="label-2">{dictionary.registries.owner}</label>
          {item.owner && (
            <>
              <img id={"owner_" + index} src={item.owner.logoDarkUrl} />
              <UncontrolledTooltip placement="right" target={"owner_" + index}>
                <label className="label-2 text-light">
                  {dictionary.registries.company}:
                </label>
                <label className="d-block">{item.owner.name || "-"}</label>
                {item.owner.externalReference && (
                  <label className="fs-xs">
                    ({item.owner.externalReference})
                  </label>
                )}
              </UncontrolledTooltip>
            </>
          )}
        </div>

        <div style={{ gridArea: "carrier" }} className="">
          <img
            id={"carrier_" + index}
            src={item.carrierConfiguration?.carrierLogo}
          />
          <UncontrolledTooltip placement="right" target={"carrier_" + index}>
            <label className="label-2 text-light">
              {dictionary.registries.company}:
            </label>
            <label className="d-block">
              {item.carrierConfiguration?.carrierName}
            </label>
          </UncontrolledTooltip>
        </div>

        <div style={{ gridArea: "name" }}>
          <label className="label-2">{dictionary.registries.name}</label>
          <label className="label-3">{item.name || "-"}</label>
        </div>

        <div style={{ gridArea: "description" }}>
          <label className="label-2">{dictionary.registries.description}</label>
          <label className="label-3">{item.description || "-"}</label>
        </div>

        <div style={{ gridArea: "validity" }}>
          <label className="label-2">{dictionary.times.validity}</label>
          <label className="label-3">
            {item.validityFrom && item.validityTo
              ? `${moment(item.validityFrom).format("DD/MM/YYYY")} - ${moment(
                  item.validityTo
                ).format("DD/MM/YYYY")}`
              : "-"}
          </label>
        </div>

        <div style={{ gridArea: "service" }}>
          <label className="label-2">{dictionary.services.services}</label>
          <label className="label-3">
            {item.ownerId ? (
              <Link
                className="text-center"
                to={`/contract-services?contractId=${item.id}`}
              >
                {item.totalServices || "-"}
              </Link>
            ) : (
              "-"
            )}
          </label>
        </div>

        {isSmall && <hr style={{ gridArea: "row" }} className="my-1" />}

        <div className="text-end" style={{ gridArea: "action" }}>
          <button
            title={dictionary.actions.edit}
            className={"button-1 button-1-light button-1-icon me-1"}
            onClick={() => {
              openModal("edit", [item.id], "md", "", dictionary.actions.edit);
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>

          <button
            title={dictionary.actions.duplicate}
            className="button-1 button-1-light button-1-icon me-1"
            onClick={() => {
              openModal(
                "clone",
                [item.id],
                "md",
                "",
                dictionary.actions.duplicate
              );
            }}
          >
            <FontAwesomeIcon icon={faClone} />
          </button>

          <button
            title={dictionary.actions.delete}
            className="button-1 button-1-danger button-1-icon"
            onClick={() => {
              openModal(
                "delete",
                [item.id],
                "sm",
                "",
                dictionary.actions.delete
              );
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
    </div>
  );
});
